import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authLogin } from '../api/authLogin';
import {
  FieldState,
  UseLoginFormReturn,
  ValidationResult,
  UseLoginFormProps,
} from '../types/loginTypes';

export const useLoginForm = ({
  onLoginSuccess,
}: UseLoginFormProps = {}): UseLoginFormReturn => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [emailField, setEmailField] = useState<FieldState>({
    hasError: false,
    errorMessage: '',
    value: '',
  });
  const [passwordField, setPasswordField] = useState<FieldState>({
    hasError: false,
    errorMessage: '',
    value: '',
  });
  const [globalError, setGlobalError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const validateEmail = useCallback((email: string): ValidationResult => {
    if (!email.trim()) {
      return { isValid: false, message: 'To pole jest wymagane' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return {
        isValid: false,
        message: 'Nieprawidłowy format adresu e-mail',
      };
    }
    return { isValid: true, message: '' };
  }, []);

  const validatePassword = useCallback((password: string): ValidationResult => {
    if (!password) {
      return { isValid: false, message: 'To pole jest wymagane' };
    }
    return { isValid: true, message: '' };
  }, []);

  // Handlers
  // Computed display value for password (asterisks when hidden)
  const displayPassword = showPassword
    ? passwordField.value
    : '*'.repeat(passwordField.value.length);

  const handleTogglePassword = useCallback(() => {
    const input = passwordInputRef.current;
    const cursorPosition = input?.selectionStart ?? 0;

    setShowPassword((prev) => !prev);

    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      input?.setSelectionRange(cursorPosition, cursorPosition);
    });
  }, []);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\s/g, '');
      setEmailField((prev) => ({
        ...prev,
        value,
        hasError: false,
        errorMessage: '',
      }));
      if (globalError) {
        setPasswordField((prev) => ({
          ...prev,
          hasError: false,
          errorMessage: '',
        }));
        setGlobalError(false);
      }
    },
    [globalError]
  );

  const handleEmailClear = useCallback(() => {
    setEmailField((prev) => ({
      ...prev,
      value: '',
      hasError: false,
      errorMessage: '',
    }));
  }, []);

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const newDisplayValue = input.value;
      const cursorPos = input.selectionStart ?? 0;

      setPasswordField((prev) => {
        const oldValue = prev.value;
        const oldLength = oldValue.length;
        const newLength = newDisplayValue.length;

        let newValue: string;

        if (showPassword) {
          newValue = newDisplayValue;
        } else {
          if (newLength > oldLength) {
            const addedChars = newDisplayValue.slice(
              cursorPos - (newLength - oldLength),
              cursorPos
            );
            newValue =
              oldValue.slice(0, cursorPos - (newLength - oldLength)) +
              addedChars +
              oldValue.slice(cursorPos - (newLength - oldLength));
          } else if (newLength < oldLength) {
            const deleteCount = oldLength - newLength;
            newValue =
              oldValue.slice(0, cursorPos) +
              oldValue.slice(cursorPos + deleteCount);
          } else {
            const typedChar = newDisplayValue[cursorPos - 1] || '';
            if (typedChar !== '*') {
              newValue =
                oldValue.slice(0, cursorPos - 1) +
                typedChar +
                oldValue.slice(cursorPos);
            } else {
              newValue = oldValue;
            }
          }
        }

        return {
          ...prev,
          value: newValue,
          hasError: false,
          errorMessage: '',
        };
      });

      if (globalError) {
        setEmailField((prev) => ({
          ...prev,
          hasError: false,
          errorMessage: '',
        }));
        setGlobalError(false);
      }

      requestAnimationFrame(() => {
        input.setSelectionRange(cursorPos, cursorPos);
      });
    },
    [globalError, showPassword]
  );

  const handleSubmit = useCallback(
    async (
      e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
    ) => {
      e.preventDefault();
      setIsSubmitting(true);

      const emailValidation = validateEmail(emailField.value);
      const passwordValidation = validatePassword(passwordField.value);

      let hasErrors = false;

      if (!emailValidation.isValid) {
        setEmailField((prev) => ({
          ...prev,
          hasError: true,
          errorMessage: emailValidation.message,
        }));
        hasErrors = true;
      }

      if (!passwordValidation.isValid) {
        setPasswordField((prev) => ({
          ...prev,
          hasError: true,
          errorMessage: passwordValidation.message,
        }));
        hasErrors = true;
      }

      if (!hasErrors) {
        setTimeout(async () => {
          const _result = await authLogin({
            email: emailField.value,
            password: passwordField.value,
          });
          if (_result.error) {
            // Check if it's a deactivated account error
            if (
              _result.error === 'ACCOUNT_DEACTIVATED' ||
              _result.error === 'PAYMENT_PENDING'
            ) {
              setGlobalError(true);
              setEmailField((prev) => ({
                ...prev,
                hasError: false,
                errorMessage: '',
              }));
              setPasswordField((prev) => ({
                ...prev,
                hasError: true,
                errorMessage:
                  'Twoje konto zostało dezaktywowane. Skontaktuj się z administratorem systemu.',
              }));
            } else {
              // Other login errors
              setGlobalError(true);
              setEmailField((prev) => ({
                ...prev,
                hasError: true,
                errorMessage: '',
              }));
              setPasswordField((prev) => ({
                ...prev,
                hasError: true,
                errorMessage: '',
              }));
            }
          } else if (_result.data) {
            // Check for challenge types
            const nextStep = _result.data.nextStep;

            if (nextStep) {
              // Check for NEW_PASSWORD_REQUIRED challenge
              if (
                nextStep.signInStep ===
                'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
              ) {
                // Redirect to new password required page with challenge state
                navigate('/new-password-required', {
                  replace: true,
                  state: {
                    challengeState:
                      'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
                  },
                });
                setIsSubmitting(false);
                return;
              }

              // Check for CUSTOM_CHALLENGE (OTP)
              if (
                nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
              ) {
                // Switch to OTP view
                onLoginSuccess?.(emailField.value);
                setIsSubmitting(false);
                return;
              }
            }

            // If signed in successfully without challenges
            // if (_result.data.isSignedIn) {
            //   navigate('/administrators', { replace: true });
            //   setIsSubmitting(false);
            //   return;
            // }

            // Default: treat as OTP challenge if nextStep exists
            onLoginSuccess?.(emailField.value);
          }
          setIsSubmitting(false);
        }, 2000);
      } else {
        setIsSubmitting(false);
      }
    },
    [
      emailField.value,
      passwordField.value,
      validateEmail,
      validatePassword,
      onLoginSuccess,
      navigate,
    ]
  );

  return {
    showPassword,
    emailField,
    passwordField,
    displayPassword,
    globalError,
    isSubmitting,
    passwordInputRef,
    handleTogglePassword,
    handleEmailChange,
    handleEmailClear,
    handlePasswordChange,
    handleSubmit,
  };
};
